import { Controller } from '@hotwired/stimulus';

// Topbar global search. Handles dropdown visibility, the Cmd/Ctrl+K focus
// shortcut, arrow-key navigation of the dropdown items, and re-running a
// previous search. The dropdown contents (recent searches and results) are
// rendered server-side and loaded via Turbo into the #search-dropdown frame.
export default class extends Controller {
    static targets = ['input', 'dropdown', 'form', 'shortcut'];
    static values = { recentUrl: String };

    connect() {
        this.activeIndex = -1;
        this.navDropdowns = document.querySelectorAll('.navbar-custom .dropdown:not(.app-search)');

        // Bind handlers once so they can be removed on disconnect.
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onNavDropdownShow = () => this.close();

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.navDropdowns.forEach((dropdown) => {
            dropdown.addEventListener('show.bs.dropdown', this.onNavDropdownShow);
        });

        this.renderShortcutHint();
    }

    disconnect() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.navDropdowns.forEach((dropdown) => {
            dropdown.removeEventListener('show.bs.dropdown', this.onNavDropdownShow);
        });
    }

    // Keep the dropdown open while interacting anywhere inside the search
    // component (input, recent items); close it when clicking elsewhere.
    onDocumentClick(event) {
        if (this.element.contains(event.target)) {
            this.open();
        } else {
            this.close();
        }
    }

    // Cmd+K (mac) / Ctrl+K (everything else) jumps to the search input.
    onKeydown(event) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            this.inputTarget.focus();
            this.inputTarget.select();
            this.open();
        }
    }

    // Arrow keys move a highlight through the dropdown items; Enter follows the
    // highlighted item (otherwise it falls through to submit the search form).
    onInputKeydown(event) {
        const items = this.items();

        switch (event.key) {
            case 'ArrowDown':
                if (!items.length) return;
                event.preventDefault();
                // Ensure visible without reloading the frame (a reload would
                // replace the items and drop the highlight).
                this.dropdownTarget.classList.add('d-block');
                this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
                this.highlight(items);
                break;
            case 'ArrowUp':
                if (!items.length) return;
                event.preventDefault();
                this.activeIndex = Math.max(this.activeIndex - 1, -1);
                this.highlight(items);
                break;
            case 'Enter':
                if (items[this.activeIndex]) {
                    event.preventDefault();
                    items[this.activeIndex].click();
                }
                break;
            case 'Escape':
                this.close();
                break;
        }
    }

    open() {
        // Close any open navbar dropdowns so they don't overlap the results.
        this.navDropdowns.forEach((dropdown) => {
            dropdown.querySelector('.dropdown-menu.show')?.classList.remove('show');
        });
        if (!this.inputTarget.value.trim()) this.loadRecentSearches();
        this.dropdownTarget.classList.add('d-block');
    }

    close() {
        this.dropdownTarget.classList.remove('d-block');
        this.resetHighlight();
    }

    // Restore the recent-searches list when the field is cleared.
    onInput() {
        this.resetHighlight();
        if (!this.inputTarget.value.trim()) this.loadRecentSearches();
    }

    // Re-run a previous search by populating the field and submitting the form.
    selectRecent(event) {
        event.preventDefault();
        this.inputTarget.value = event.currentTarget.dataset.query;
        this.formTarget.requestSubmit();
    }

    // Load the recent-searches frame from the server (the dropdown's default
    // state). Setting src triggers the first load; reload() refreshes it after
    // a search stream has replaced the frame contents with results.
    loadRecentSearches() {
        if (!this.hasRecentUrlValue) return;

        if (this.dropdownTarget.src) {
            this.dropdownTarget.reload();
        } else {
            this.dropdownTarget.src = this.recentUrlValue;
        }
    }

    // ---- keyboard highlighting -------------------------------------------

    items() {
        return Array.from(this.dropdownTarget.querySelectorAll('a.dropdown-item'));
    }

    highlight(items) {
        items.forEach((item, index) => {
            const active = index === this.activeIndex;
            item.classList.toggle('active', active);
            if (active) item.scrollIntoView({ block: 'nearest' });
        });
    }

    resetHighlight() {
        this.activeIndex = -1;
        this.items().forEach((item) => item.classList.remove('active'));
    }

    renderShortcutHint() {
        if (!this.hasShortcutTarget) return;
        const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
        this.shortcutTarget.textContent = isMac ? '⌘K' : 'Ctrl K';
    }
}
